// Capture phase — "Chụp 3 biểu cảm khuôn mặt" (new flow, custom path).

import type { Context, Emotion, Phase } from "../state/types";
import type { FaceLandmarker } from "@mediapipe/tasks-vision";
import { mountFrame, clearChildren } from "../ui/overlay";
import {
  captureVideoRegionAsPng,
  checkFramingArea2,
  computeCoverMap,
  ensureLandmarker,
  getCamera,
  headPoseFromMatrix,
  requestCamera,
} from "../capture";
import type { CoverMap, DisplayRect, HeadPose } from "../capture";
import { upload, MaintenanceError } from "../api/client";
import type { UploadFailReason } from "../api/types";
import { getAudioBus } from "../audio";

const FLOW = "/textures/flow";
const EMOTIONS: Emotion[] = ["neutral", "happy", "sad"];
const EMOTION_ICON: Record<Emotion, string> = {
  neutral: "iconEmotionNeitral",
  happy: "iconEmotionHappy",
  sad: "iconEmotionSad",
};
const EMOTION_PROMPT_VI: Record<Emotion, string> = {
  neutral: "Giữ vẻ trung tính",
  happy: "Hãy cười tươi",
  sad: "Tỏ vẻ buồn",
};
const UPLOAD_FAIL_REASON_VI: Record<UploadFailReason, string> = {
  nsfw: "Ảnh không phù hợp",
  face_not_detected: "Không tìm thấy khuôn mặt rõ",
  headwear_detected: "Vui lòng tháo mũ hoặc khăn",
  mask_detected: "Vui lòng tháo khẩu trang",
};

const VIEWFINDER = "top:11.5%;left:7%;width:85%;height:54%;";
// Slots centered in the gap between the baked "Hãy chọn…" text (ends ~73.5%) and
// the baked agreement text (~88.7%) → container centered at ~81%.
const SLOTS_ROW = "top:75%;left:8%;width:84%;height:12%;";
// Consent row lowered so the checkbox centers on the baked agreement text (~89.9%).
const CONSENT_ROW = "top:87.15%;left:5%;width:90%;height:5.5%;";
// Circular capture-trigger button (new design), centered on the viewfinder's
// bottom edge (~65.5%) so it straddles the border half-in/half-out per the design
// ref. The emotion slots select which emotion to capture; this button triggers
// the shot. It lives in #panel (not the overflow-hidden viewfinder) so it isn't
// clipped. NOTE: frameCapture.png's baked "Hãy chọn…" text was nudged down to
// clear this button — keep them in sync if either moves. Tune top/width here.
const CAPTURE_BTN = "left:50%;top:65.5%;width:14%;transform:translate(-50%,-50%);";
// After a shot, the button is deactivated this long to avoid a double-tap
// double-capture (it stays visible, just dimmed + non-clickable).
const CAPTURE_COOLDOWN_MS = 1000;
const SLOT_WIDTH = "22%";
const LOGO_WIDTH = "85%";

function characterAssetUrl(ctx: Context, slot: Emotion): string | null {
  return ctx.assets.character?.[slot] ?? null;
}

let cleanup: (() => void) | null = null;

export const Capture: Phase = {
  async enter(ctx, transition) {
    const root = document.getElementById("app")!;
    clearChildren(root);

    const bg = document.createElement("div");
    bg.className = "app-bg";
    bg.style.backgroundImage = "url(/textures/bg_indexA.png)";
    bg.style.backgroundColor = "#f7c7d8";

    const frame = document.createElement("div");
    frame.className = "app-frame";
    frame.style.cssText =
      "position:relative;display:flex;flex-direction:column;align-items:center;padding:2% 4%;box-sizing:border-box;gap:1.5%;";

    frame.innerHTML = `
      <img src="${FLOW}/LogoHeader.png" alt="" style="width:${LOGO_WIDTH};object-fit:contain;flex:0 0 auto;"/>
      <div style="flex:1 1 auto;min-height:0;width:100%;display:flex;align-items:center;justify-content:center;">
        <div id="panel" style="position:relative;height:100%;width:auto;max-width:100%;aspect-ratio:1500/2332;flex:0 0 auto;
          background:url(${FLOW}/frameCapture.png) center/100% 100% no-repeat;">
          <div id="viewfinder" style="position:absolute;${VIEWFINDER}border-radius:16px;overflow:hidden;background:#caa;">
            <video id="cam" autoplay playsinline muted style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;transform:scaleX(-1);display:none;"></video>
            <img id="faceGuide" src="${FLOW}/Group 97.png" alt="" style="position:absolute;inset:0;width:100%;height:100%;object-fit:contain;pointer-events:none;"/>
            <div id="vfhint" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;text-align:center;color:#7a2a3a;font-size:13px;font-weight:bold;padding:8%;">Chạm vào một biểu cảm để bắt đầu</div>
          </div>
          <button id="captureBtn" style="position:absolute;${CAPTURE_BTN}display:none;background:none;border:0;padding:0;cursor:pointer;z-index:15;
            background-image:url(/textures/captureTriggerBtn.png);background-size:contain;background-repeat:no-repeat;background-position:center;aspect-ratio:232/224;" aria-label="Chụp"></button>
          <div id="slots" style="position:absolute;${SLOTS_ROW}display:flex;gap:5%;align-items:center;justify-content:center;"></div>
          <button id="consent" style="position:absolute;${CONSENT_ROW}display:flex;align-items:center;gap:3%;background:none;border:0;cursor:pointer;padding:0;">
            <div id="cbox" style="height:78%;aspect-ratio:1;border:2px solid #e23b6d;border-radius:50%;flex:0 0 auto;background:center/contain no-repeat;"></div>
            <div style="flex:1;"></div>
          </button>
        </div>
      </div>
      <button id="continue" disabled style="
        width:55%;background:none;border:0;padding:0;cursor:pointer;opacity:0.5;
        background-image:url(${FLOW}/buttonContinue.png);background-size:contain;background-repeat:no-repeat;
        background-position:center;aspect-ratio:612/208;transition:opacity 120ms;" aria-label="Tiếp tục"></button>
    `;

    root.appendChild(bg);
    const frameCleanup = mountFrame(root, frame);

    const video = frame.querySelector<HTMLVideoElement>("#cam")!;
    const vfHint = frame.querySelector<HTMLElement>("#vfhint")!;
    const faceGuide = frame.querySelector<HTMLElement>("#faceGuide")!;
    const slotsEl = frame.querySelector<HTMLElement>("#slots")!;
    const consentBtn = frame.querySelector<HTMLButtonElement>("#consent")!;
    const cbox = frame.querySelector<HTMLElement>("#cbox")!;
    const continueBtn = frame.querySelector<HTMLButtonElement>("#continue")!;
    const captureBtn = frame.querySelector<HTMLButtonElement>("#captureBtn")!;

    type SlotState = "empty" | "captured" | "uploaded";
    const slotState: Record<Emotion, SlotState> = { neutral: "empty", happy: "empty", sad: "empty" };
    for (const e of EMOTIONS) {
      slotState[e] = ctx.capture[e] ? "captured" : characterAssetUrl(ctx, e) ? "uploaded" : "empty";
    }
    // Snapshot at entry: a returning player already has uploaded faces. New
    // players auto-advance through the poses; returning players stay on the pose
    // they recapture.
    const isNewPlayer = !EMOTIONS.some((e) => slotState[e] === "uploaded");
    let active: Emotion | null = null;
    let captureCooldown = false;
    let consent = true;
    let isFed = !!getCamera();
    let landmarker: FaceLandmarker | null = null;
    let coverMap: CoverMap | null = null;
    let a2RectRel: DisplayRect | null = null;
    let inFrame = false;
    let lastHeadPose: HeadPose | null = null;
    let lastVideoTime = -1;
    let rafId = 0;
    let stopped = false;
    let processing = false;
    let requestingFeed = false;
    const slotBlobUrls: Partial<Record<Emotion, string>> = {};

    const existing = getCamera();
    if (existing) video.srcObject = existing;
    video.style.display = isFed ? "block" : "none";

    cleanup = () => {
      stopped = true;
      cancelAnimationFrame(rafId);
      try {
        video.pause();
        video.srcObject = null;
      } catch {
        /* ignore */
      }
      for (const u of Object.values(slotBlobUrls)) if (u) URL.revokeObjectURL(u);
      if (bg.parentElement === root) root.removeChild(bg);
      frameCleanup();
    };

    const recomputeGeometry = () => {
      coverMap = computeCoverMap(video);
      const r = video.getBoundingClientRect();
      a2RectRel = r.width && r.height ? { x: 0, y: 0, w: r.width, h: r.height } : null;
    };
    video.addEventListener("loadedmetadata", recomputeGeometry);
    video.addEventListener("resize", recomputeGeometry);
    window.addEventListener("resize", recomputeGeometry);

    const paintConsent = () => {
      if (consent) {
        cbox.style.background = `center/contain no-repeat url(${FLOW}/checkIcon.png)`;
        cbox.style.border = "0";
      } else {
        cbox.style.background = "transparent";
        cbox.style.border = "2px solid #e23b6d";
      }
    };
    consentBtn.addEventListener("click", () => {
      consent = !consent;
      paintConsent();
      repaintSlots();
      refreshContinue();
    });
    paintConsent();

    const slotEls: Partial<Record<Emotion, HTMLButtonElement>> = {};
    EMOTIONS.forEach((e) => {
      const slot = document.createElement("button");
      slot.dataset.emotion = e;
      slot.style.cssText =
        `width:${SLOT_WIDTH};aspect-ratio:300/300;background:none;border:0;padding:0;cursor:pointer;position:relative;`;
      slot.innerHTML = `
        <div class="sbg" style="position:absolute;inset:0;background:url(${FLOW}/FrameEmotion.png) center/100% 100% no-repeat;"></div>
        <div class="sicon" style="position:absolute;inset:14%;background:url(${FLOW}/${EMOTION_ICON[e]}.png) center/contain no-repeat;"></div>
        <div class="sdone" style="position:absolute;top:2%;right:4%;width:26%;aspect-ratio:1;background:url(${FLOW}/checkIcon.png) center/contain no-repeat;display:none;"></div>
      `;
      slot.addEventListener("click", () => onSlotTap(e));
      slotsEl.appendChild(slot);
      slotEls[e] = slot;
    });

    const paintSlot = (e: Emotion) => {
      const el = slotEls[e]!;
      const sbg = el.querySelector<HTMLElement>(".sbg")!;
      const sicon = el.querySelector<HTMLElement>(".sicon")!;
      const sdone = el.querySelector<HTMLElement>(".sdone")!;
      const st = slotState[e];
      const isActive = active === e;
      sbg.style.backgroundImage = `url(${FLOW}/${isActive ? "frameEmotionSelected" : "FrameEmotion"}.png)`;
      if (st === "captured" && ctx.capture[e]) {
        sicon.style.background = `url(${slotImageUrl(e)}) center/cover no-repeat`;
        sicon.style.borderRadius = "10px";
      } else if (st === "uploaded") {
        const url = characterAssetUrl(ctx, e);
        sicon.style.background = url ? `url(${url}) center/cover no-repeat` : "";
        sicon.style.borderRadius = "10px";
      } else {
        sicon.style.background = `url(${FLOW}/${EMOTION_ICON[e]}.png) center/contain no-repeat`;
        sicon.style.borderRadius = "0";
      }
      sdone.style.display = st === "empty" ? "none" : "block";
      const ready = isActive && isFed && inFrame && consent && st !== "uploaded";
      el.style.filter = ready ? "drop-shadow(0 0 8px #4caf50)" : "none";
      el.style.transform = isActive ? "scale(1.08)" : "scale(1)";
    };
    // Capture button shows whenever an emotion is selected and the camera is
    // live — always discoverable (fixes "didn't know where to press"). It's
    // dimmed + non-clickable until the face aligns, then full + clickable.
    // Recapture of an already-uploaded emotion is allowed (returning users).
    // Consent is NOT required to show it; the click flashes the consent box.
    const updateCaptureBtn = () => {
      const armed = active !== null && isFed && !processing;
      const ready = armed && inFrame && !captureCooldown;
      captureBtn.style.display = armed ? "block" : "none";
      captureBtn.style.opacity = ready ? "1" : "0.5";
      captureBtn.style.pointerEvents = ready ? "auto" : "none";
    };
    const repaintSlots = () => {
      EMOTIONS.forEach(paintSlot);
      updateCaptureBtn();
    };

    captureBtn.addEventListener("click", () => {
      if (stopped || processing || active === null || !isFed || !inFrame) return;
      if (!consent) {
        flashConsent();
        return;
      }
      void capture(active);
    });

    const slotImageUrl = (e: Emotion): string => {
      let u = slotBlobUrls[e];
      if (!u && ctx.capture[e]) {
        u = URL.createObjectURL(ctx.capture[e]!);
        slotBlobUrls[e] = u;
      }
      return u ?? "";
    };
    const revokeSlotUrl = (e: Emotion) => {
      const u = slotBlobUrls[e];
      if (u) {
        URL.revokeObjectURL(u);
        delete slotBlobUrls[e];
      }
    };

    const refreshContinue = () => {
      const allReady = EMOTIONS.every((e) => slotState[e] !== "empty");
      continueBtn.disabled = !allReady || !consent || processing;
      continueBtn.style.opacity = continueBtn.disabled ? "0.5" : "1";
      continueBtn.textContent = processing ? "Đang xử lý…" : "";
    };

    const waitForVideoReady = (): Promise<void> => {
      if (video.readyState >= 2) return Promise.resolve();
      return new Promise<void>((resolve) => {
        const done = () => {
          video.removeEventListener("loadeddata", done);
          clearTimeout(t);
          resolve();
        };
        const t = window.setTimeout(done, 6000);
        video.addEventListener("loadeddata", done);
      });
    };
    const ensureLandmarkerReady = async (): Promise<void> => {
      if (!landmarker) landmarker = await ensureLandmarker();
    };
    const requestFeed = async (): Promise<boolean> => {
      if (isFed) return true;
      if (requestingFeed) return false;
      requestingFeed = true;
      const cover = document.createElement("div");
      cover.style.cssText =
        "position:absolute;inset:0;background:#0008;z-index:20;display:flex;align-items:center;justify-content:center;color:#fff;font-size:16px;font-weight:bold;";
      cover.textContent = "Đang xử lý…";
      root.appendChild(cover);
      try {
        let stream: MediaStream;
        try {
          stream = await requestCamera();
        } catch (err) {
          console.error("[Capture] camera permission denied/failed", err);
          if (!stopped) showWarning("Bạn cần <strong>cấp quyền</strong> truy cập <strong>camera</strong> để chụp hình tạo nhân vật.");
          return false;
        }
        video.srcObject = stream;
        await video.play().catch(() => {});
        try {
          await Promise.all([waitForVideoReady(), ensureLandmarkerReady()]);
        } catch (err) {
          console.error("[Capture] face model load failed", err);
          if (!stopped) showWarning("Không tải được mô hình nhận diện khuôn mặt. Vui lòng thử lại.");
          return false;
        }
        if (stopped) return false;
        isFed = true;
        inFrame = false;
        video.style.display = "block";
        vfHint.style.display = "none";
        recomputeGeometry();
        updateGuide();
        return true;
      } finally {
        cover.remove();
        requestingFeed = false;
      }
    };

    async function onSlotTap(e: Emotion): Promise<void> {
      if (stopped || processing) return;
      if (slotState[e] === "uploaded" && active !== e) {
        active = e;
        repaintSlots();
        return;
      }
      if (active !== e) {
        active = e;
        inFrame = false;
        setInstruction(e);
        repaintSlots();
        updateGuide();
        await requestFeed();
        repaintSlots();
        return;
      }
      // Tapping the already-selected slot just (re)starts the feed if needed —
      // the capture itself is triggered by the dedicated #captureBtn.
      if (!isFed) {
        await requestFeed();
        repaintSlots();
      }
    }

    function setInstruction(_e: Emotion): void {
      vfHint.style.display = "none";
    }

    function flashConsent(): void {
      cbox.animate(
        [{ outline: "0 solid #e23b6d" }, { outline: "4px solid #e23b6d" }, { outline: "0 solid #e23b6d" }],
        { duration: 600 },
      );
    }

    async function capture(e: Emotion): Promise<void> {
      try {
        recomputeGeometry();
        const r = video.getBoundingClientRect();
        const cropRect: DisplayRect = { x: 0, y: 0, w: r.width, h: r.height };
        const blob = await captureVideoRegionAsPng(video, cropRect);
        revokeSlotUrl(e);
        ctx.capture[e] = blob;
        slotState[e] = "captured";
        try {
          getAudioBus().playShutter();
        } catch {
          /* audio not ready */
        }
        // Old flow: new players auto-advance to the next empty pose; returning
        // players stay on the pose they just recaptured. Never null out `active`
        // — the button stays visible after a shot.
        if (isNewPlayer) {
          const next = EMOTIONS.find((x) => slotState[x] === "empty");
          if (next) active = next;
        }
        // Deactivate briefly to avoid a double-tap double-capture.
        captureCooldown = true;
        repaintSlots();
        refreshContinue();
        window.setTimeout(() => {
          captureCooldown = false;
          if (!stopped) updateCaptureBtn();
        }, CAPTURE_COOLDOWN_MS);
      } catch (err) {
        console.error("[Capture] frame capture failed", err);
      }
    }

    const updateGuide = () => {
      faceGuide.style.display = isFed && !inFrame ? "block" : "none";
    };
    const tick = () => {
      if (stopped) return;
      rafId = requestAnimationFrame(tick);
      if (
        active &&
        isFed &&
        landmarker &&
        video.readyState >= 2 &&
        video.currentTime !== lastVideoTime
      ) {
        lastVideoTime = video.currentTime;
        if (!coverMap || !a2RectRel) recomputeGeometry();
        const result = landmarker.detectForVideo(video, performance.now());
        const landmarks = result.faceLandmarks?.[0];
        lastHeadPose = headPoseFromMatrix(result.facialTransformationMatrixes?.[0]);
        const framing = checkFramingArea2(landmarks, coverMap, a2RectRel, lastHeadPose);
        if (framing.ok !== inFrame) {
          inFrame = framing.ok;
          repaintSlots();
          updateGuide();
        }
      }
    };

    async function doUpload(): Promise<void> {
      processing = true;
      refreshContinue();
      try {
        const payload: Parameters<typeof upload>[0] = {
          uid: ctx.uid,
          isMale: ctx.isMale ?? true,
          sessionId: ctx.sessionId,  // ← truyền sessionId
        };
        const sent: Emotion[] = [];
        for (const e of EMOTIONS) {
          if (ctx.capture[e]) {
            payload[e] = ctx.capture[e];
            sent.push(e);
          }
        }
        if (sent.length === 0) {
          transition("Review");
          return;
        }
        const res = await upload(payload);
        if ("failed" in res) {
          showErrorModal(res.failed as Emotion[], res.reasons);
          return;
        }
        ctx.assets.character = res.assets;
        for (const e of sent) {
          slotState[e] = "uploaded";
          delete ctx.capture[e];
          revokeSlotUrl(e);
        }
        transition("Review");
      } catch (err) {
        if (err instanceof MaintenanceError) {
          transition("Maintenance");
          return;
        }
        console.error("[Capture] upload failed", err);
        showErrorModal([], {}, true);
      } finally {
        if (!stopped) {
          processing = false;
          refreshContinue();
        }
      }
    }

    continueBtn.addEventListener("click", () => {
      if (continueBtn.disabled || processing) return;
      void doUpload();
    });

    function showErrorModal(
      failed: Emotion[],
      reasons: Partial<Record<Emotion, UploadFailReason>>,
      network = false,
    ): void {
      const modal = document.createElement("div");
      modal.style.cssText =
        "position:absolute;inset:0;background:#0009;z-index:30;display:flex;align-items:center;justify-content:center;padding:24px;";
      const lines = network
        ? ["Lỗi mạng hoặc máy chủ — vui lòng thử lại."]
        : failed.map((e) => {
            const r = reasons[e];
            return `<strong>${EMOTION_PROMPT_VI[e]}:</strong> ${r ? UPLOAD_FAIL_REASON_VI[r] : "Ảnh chưa phù hợp"}`;
          });
      modal.innerHTML = `
        <div style="background:#fff;color:#222;border-radius:14px;padding:20px;max-width:90%;display:flex;flex-direction:column;gap:14px;align-items:center;">
          <div style="font-size:16px;font-weight:bold;">Ảnh này chưa phù hợp rồi</div>
          <div style="font-size:14px;line-height:1.5;text-align:center;">${lines.map((l) => `<div>${l}</div>`).join("")}</div>
          <div style="font-size:13px;opacity:0.7;">Chụp lại để tiếp tục nhé!</div>
          <button id="retry" style="width:60%;background:none;border:0;padding:0;cursor:pointer;
            background-image:url(${FLOW}/buttonRecapture.png);background-size:contain;background-repeat:no-repeat;
            background-position:center;aspect-ratio:636/216;" aria-label="Chụp lại"></button>
        </div>`;
      root.appendChild(modal);
      modal.querySelector<HTMLButtonElement>("#retry")!.addEventListener("click", () => {
        for (const e of failed) {
          delete ctx.capture[e];
          slotState[e] = characterAssetUrl(ctx, e) ? "uploaded" : "empty";
          revokeSlotUrl(e);
        }
        modal.remove();
        processing = false;
        active = failed[0] ?? EMOTIONS.find((e) => slotState[e] === "empty") ?? null;
        repaintSlots();
        refreshContinue();
      });
    }

    function showWarning(bodyHtml: string): void {
      const modal = document.createElement("div");
      modal.style.cssText =
        "position:absolute;inset:0;background:#0009;z-index:30;display:flex;align-items:center;justify-content:center;padding:24px;";
      modal.innerHTML = `
        <div style="background:#fff;color:#222;border-radius:14px;padding:20px;max-width:90%;display:flex;flex-direction:column;gap:14px;">
          <div style="font-size:16px;font-weight:bold;">Không thể chụp hình</div>
          <div style="font-size:14px;line-height:1.5;">${bodyHtml}</div>
          <button id="ok" style="padding:12px 20px;border:0;border-radius:10px;background:#e34;color:#fff;font-size:15px;font-weight:bold;cursor:pointer;align-self:flex-end;">Đồng ý</button>
        </div>`;
      root.appendChild(modal);
      modal.querySelector<HTMLButtonElement>("#ok")!.addEventListener("click", () => modal.remove());
    }

    active = EMOTIONS.find((e) => slotState[e] === "empty") ?? "neutral";
    repaintSlots();
    refreshContinue();
    updateGuide();
    if (isFed) {
      void ensureLandmarkerReady().catch((err) => console.error("[Capture] model warm failed", err));
      vfHint.style.display = "none";
    }
    void tick();
  },
  exit() {
    cleanup?.();
    cleanup = null;
  },
};
